import { useAdminSession } from '@/lib/admin-session';
import { fetchCriteria, fetchProtocol, fetchProtocolVersion } from '@/lib/api';
import type { ExperienceDraft } from '@/lib/experience-draft';
import { draftAsContent } from '@/lib/experience-draft';
import type { VariableScope, VariableSources } from '@/lib/variable-pill';
import { NO_VARIABLE_SCOPE } from '@/lib/variable-pill';
import type { VersionPin, VersionedRef } from '@matter_extractor/shared';
import { protocolPageOf } from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, createContext, useContext, useMemo } from 'react';

/**
 * Ce qu'une expérience LIT pour nommer ses variables, tenu une fois par écran
 * (refonte modulaire §8.6 ; grill Variables du 2026-09-16, D3 à D6 ; issue #88).
 *
 * Two of the four kinds of variable reach OUTSIDE the experience: an objective
 * is named by the protocol its protocol page plays, a criterion by its own
 * entity in the library. Every zone that draws a pill needs both reads - the
 * WYSIWYG of a block, a Checkbox label, the Contexte, the overlay, the
 * read-only panel, the previews - and they sit three to five components deep
 * in three different tabs. So the reads are made ONCE, here, and handed down
 * by a context: the screens compose them with the draft they already hold
 * (`useVariableSources`) and never fetch a protocol themselves.
 *
 * ## The protocol is read at the PIN the page plays
 *
 * `latest` - the default of every reference (vitrine.md §3.1, V16) - is the
 * DEVELOPMENT version the detail carries; a pinned number is that very version,
 * read by its id off the detail when it is not the development one - the
 * arbitrage `experience-persona-tab.tsx` already makes for the persona. Two
 * reads for a pinned older version, and both are the keys the Protocoles screens
 * already write, deliberately: same fetcher, same schema, same shape (issue #16).
 *
 * ## Read off the DRAFT, so the palette moves with the gesture
 *
 * The reference is taken from the draft's protocol page and not from the
 * record: choosing a protocol in the inspector moves the palette at once
 * rather than one autosave later. The query key carries the id alone, so
 * typing anywhere else in the draft never refetches anything.
 *
 * ## Outside any scope, the protocol is held to be absent
 *
 * `useVariableScope` answers `NO_VARIABLE_SCOPE` when no provider is above: a
 * zone mounted alone - a test, a component shown out of its screen - then
 * names its fields and choices from the draft and shows a protocol variable as
 * unknown, which is honest, rather than throwing.
 */

const Context = createContext<VariableScope | null>(null);

/** The raw provider, for a test that wants to hand a scope in by hand. */
export const VariableScopeProvider = Context.Provider;

export function useVariableScope(): VariableScope {
  return useContext(Context) ?? NO_VARIABLE_SCOPE;
}

/** The scope of this screen composed with the draft it holds - what every zone describes from. */
export function useVariableSources(draft: ExperienceDraft): VariableSources {
  const scope = useVariableScope();
  return useMemo(
    () => ({
      draft,
      protocol: scope.protocol,
      protocolRead: scope.protocolRead,
      criteria: scope.criteria,
      criteriaRead: scope.criteriaRead,
    }),
    [draft, scope],
  );
}

/**
 * The two reads, keyed like the Protocoles and Critères screens key theirs.
 *
 * Exported for the editors' save boxes, which are rendered by the versioned
 * layer OUTSIDE this provider and still have to name a variable in a refused
 * line: they hold the record's protocol reference and call this directly.
 */
export function useExperienceVariableScope(
  protocolRef: VersionedRef | null,
  experienceId: string | null,
): VariableScope {
  const { email } = useAdminSession();
  const protocolId = protocolRef?.id ?? null;
  const pin: VersionPin = protocolRef?.pin ?? 'latest';

  const detail = useQuery({
    queryKey: ['protocol', email, protocolId],
    queryFn: () => fetchProtocol(protocolId ?? ''),
    enabled: protocolId !== null,
    retry: false,
  });
  /*
    The version a NUMBER names, when it is not the development one the detail
    already carries. `null` for `latest`, for the development number, and for a
    number the protocol does not hold - which resolves to nothing, as the
    strict resolution of §4 does at a start.
  */
  const pinned =
    detail.data === undefined || pin === 'latest' || pin === detail.data.development.number
      ? null
      : (detail.data.versions.find((version) => version.number === pin) ?? null);
  const pinnedId = pinned?.id ?? null;
  const production = useQuery({
    queryKey: ['protocol-version', email, protocolId, pinnedId],
    queryFn: () => fetchProtocolVersion(protocolId ?? '', pinnedId ?? ''),
    enabled: protocolId !== null && pinnedId !== null,
    retry: false,
  });
  const criteria = useQuery({
    queryKey: ['criteria', email],
    queryFn: fetchCriteria,
    enabled: protocolId !== null,
    retry: false,
  });

  return useMemo<VariableScope>(() => {
    if (protocolId === null) return { ...NO_VARIABLE_SCOPE, experienceId };
    const read = detail.data;
    if (read === undefined) {
      return {
        protocol: null,
        protocolRead: detail.isError,
        criteria: criteria.data?.criteria ?? [],
        criteriaRead: criteria.data !== undefined || criteria.isError,
        experienceId,
      };
    }
    /*
      Strict resolution (§4): a pin on a version the protocol does not hold
      resolves to NOTHING, so its objectives are unknown - which is also what
      the session would find at its start.
    */
    const pointsAtDevelopment = pin === 'latest' || pin === read.development.number;
    const content = pointsAtDevelopment
      ? read.developmentContent
      : pinnedId === null
        ? null
        : (production.data?.content ?? null);
    const pending = pinnedId !== null && production.data === undefined;
    return {
      protocol:
        content === null
          ? null
          : {
              id: read.id,
              name: read.name,
              pin,
              version: pointsAtDevelopment ? read.development.number : (pinned?.number ?? null),
              content,
            },
      protocolRead: !pending || production.isError,
      criteria: criteria.data?.criteria ?? [],
      criteriaRead: criteria.data !== undefined || criteria.isError,
      experienceId,
    };
  }, [
    protocolId,
    pin,
    experienceId,
    detail.data,
    detail.isError,
    pinned,
    pinnedId,
    production.data,
    production.isError,
    criteria.data,
    criteria.isError,
  ]);
}

export interface ExperienceVariableScopeProps {
  readonly draft: ExperienceDraft;
  /** The entity's id, or null before the first save: where « Ouvrir » may land. */
  readonly experienceId: string | null;
  readonly children: ReactNode;
}

/** The provider of ONE screen: it reads what the draft's protocol page names. */
export function ExperienceVariableScope({
  draft,
  experienceId,
  children,
}: ExperienceVariableScopeProps) {
  const ref = protocolPageOf(draftAsContent(draft))?.protocol ?? null;
  const scope = useExperienceVariableScope(ref, experienceId);
  return <Context.Provider value={scope}>{children}</Context.Provider>;
}
